import Channel from '@/types/Channel.interface'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import Message from '@/types/Message.interface'
import User from '@/types/User.interface'
import { mapGetters } from 'vuex'
export default {
    computed: {
        ...mapGetters({
            userTyping: 'userTyping'
        }),
        hotKeysEnabled(): boolean {
            // Checks if our hotkeys are enabled or not
            return this.$store.getters.settingGet('hotKeysEnabled')
        },
        userIds(): number[] {
            //  If we have 0 users, return an empty array
            if (!this.users) return []

            //  Else, Map the ids
            const ids = this.users.map((x: User) => x.id)

            //  Grab auth user
            const user = this.$store.getters.user

            //  Remove the current user id from this
            const filtered = ids.filter((x: number) => x != user.id)

            //  Return our filtered ids
            return filtered
        }
    },
    methods: {
        //  Assemble the channel 'dot'
        channelAction(channel: Channel): IListAction {
            //  If the channel does NOT have a last message, return nothing
            if (!channel.last_message) return null

            //  ELse, if the channel is closed, return nothing
            if (channel.closed) return null

            //  Else, determine if the channel does NOT have unread messages, AND last message was outbound
            const isReplied = !channel.unread_messages && channel.last_message.outbound

            //  If the channel is read, AND last message was outbound, return nothing
            if (isReplied) return null

            //  Else, select the action color. (depends on if the channel is waiting or not)
            const color = this.$options.filters.channelColor(channel)

            //  Assemble the 'unread messages' value
            const unreadMessages =
                channel.unread_messages > 0 ? channel.unread_messages : ''

            //  Assemble the 'unread messages' dot
            const dot = `<span style="height:18px; width:18px; border-radius:50%; color:white; font-size:10px; font-weight:bold; background-color:${color}; box-shadow: 1px 1px 3px 1px rgba(0,0,0,0.5);">
                            <span style="display:inline-flex; align-items:center;">
                                ${unreadMessages}
                            </span>
                        </span>`

            //  Else, return 'unread messages' dot
            const action: IListAction = {
                type: IListActionType.content,
                content: dot
            }

            //  return the IlistAction
            return action
        },
        disableComments(): void {
            //  Turn comment mode off
            this.commentMode = false

            //  Make sure our comment response is null
            this.commentObject = null

            //  Make sure our comment media is also null
            this.commentMedia = null

            //  Get rid of our text
            this.$refs.messageForm.reset()
        },
        enableCommentsBuffer(message: Message): void {
            //  If there is no message just return
            if (!message) return

            //  Grab the text from our message
            const text = message.text || 'MMS'

            //  Grab the timestamp from this message
            const time = this.$options.filters.chatDate(message.created_at)

            //  Format these into 1 string
            const resp = {
                author_id: message.author_id,
                response: text,
                outbound: message.outbound,
                type: message.type,
                timestamp: time
            }

            //  Save this as our response
            this.commentObject = resp

            //  Save our comment response media too
            this.commentMedia = message.media_id
        },
        enableComments(message: Message): void {
            //  Pass the message along to handle the comment response
            this.enableCommentsBuffer(message)

            //  Get rid of our text
            this.$refs.messageForm.reset()

            //  Regardless, turn comment mode on
            this.commentMode = true

            //  On the next tick, autofocus our box
            this.$nextTick(() => this.$refs.messageForm.autoFocus())
        },
        formSubmitComments(message: Message): Message {
            //  If we have a response media then we prepend it
            if (this.commentMedia) {
                //  Add the media to our message
                message.comment_media_id = this.commentMedia

                //  Remove our comment media
                this.commentMedia = null
            }

            //  Attach the comment object
            message.comment = this.commentObject
            this.commentObject = null

            //  Handle any mentions
            message = this.handleMentions(message)

            //  Grab our current path
            const path = this.$route.path

            //  If our path is NOT Teammates, then just default to inbox (account for inbox groups)
            const realPath = path.includes('teammates') ? 'teammates' : 'inbox'

            //  @ts-ignore Add our source (our path without /)
            message.source = realPath

            //  Return our modified message
            return message
        },
        //  Our handler for submitting the MessageTextForm
        formSubmit(message: Message): void {
            //  Grab the type from our message
            const type = message.type

            //  Check if this is a comment message
            const comment = type === 'comment'

            //  Handle our comment responses
            if (comment) message = this.formSubmitComments(message)

            //  Make sure our text removes weird whitespace
            if (message.text) message.text = message.text.replaceAll('﻿', '')

            //  If our message type is schedule OR if we show preview, then pop it
            const popPreview = (this.showPreview && !comment) || type === 'schedule'

            //  Pop our preview if we are set to
            if (popPreview) this.popMessagePreview(message)
            //  Else, route to sendMessage()
            else this.sendMessage(message)
        },
        handleMentions(message: Message): Message {
            //  Check if we have any Team mentions
            const team = document.querySelector('button[data-id="Team"]')

            //  Check if we have any mentions by finding the matching HTML elements
            const mentions = document.getElementsByClassName('inline-mention')

            //  If 0 mentions AND 0 Team mentions, return
            if (!mentions && !team) return message

            //  Else, create our ids container (default to userIds if we mention team)
            const ids = team ? this.userIds : []

            //  Iterate through each element
            for (let i = 0; i < mentions.length; i++) {
                //  Grab the current node
                let node = mentions[i]

                //  Grab the id from this 
                let id = node.getAttribute('data-id')

                //  Add the number version of this to our array of ids
                ids.push(+id)
            }

            //  Set our mentions (the filter removes nullish values)
            return this.setMentions(message, ids.filter(Number))
        },
        resetComments(): void {
            //  Clear our text
            this.$refs.messageForm.reset()

            //  Remove our comment response
            this.commentResponse = null

            //  Make sure our comment response is null
            this.commentObject = null

            //  Make sure our comment media is also null
            this.commentMedia = null
        },
        async sendMessage(message: Message): Promise<void> {
            //  Tell our message form to start loading (ux on send button)
            this.$refs.messageForm.sending = true

            //  Else, we don't want the preview so just send
            const response = await this.$store.dispatch('messagesPost', message)

            //  Our message is sent, tell our form to stop loading
            this.$refs.messageForm.sending = false

            //  If the message send failed, do nothing
            if (!response) return

            //  Reset the message text form
            this.$refs.messageForm.reset()
        },
        setMentions(message: Message, ids: number[] = null): Message {
            //  If comment is not already set on our message, add it
            if (!message.comment) message.comment = {}

            //  Make sure we have no duplicates
            message.comment.mentions = [...new Set(ids)]

            //  Return our message
            return message
        },
        //  Toggle the persons profile
        toggleProfile(ref: string, hotKeyPressed: boolean = false): void {
            // True/false value if hotkey was used to activate function
            const hotKeyPressedAndOn = hotKeyPressed && this.hotKeysEnabled.enabled

            // True/false value if click was used to activate function
            const clickPress = !hotKeyPressed

            // If our hotkeys aren't on and the event wasn't activated with a click we leave the function
            if (!hotKeyPressedAndOn && !clickPress) {
                return
            }

            // Change state of person profile being shown to opposite of what it is currently
            this.$refs[ref].showMore = !this.$refs[ref].showMore
        }
    }
}